import classNames from 'classnames';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import { Competency, Skill, WorkRoleSkill } from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { ActionType, ChangeState } from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { HeartBeatIcon } from '../../../../../ui/common/icons';
import { ScoreSlider } from '../../../../../ui/components/slider/score-slider';
import { Accordion } from '../../../../../ui/widgets/accordion/accordion';
import { Table, TableCell, TableRow } from '../../../../../ui/widgets/table/table';
import { LoadedMap, RoleBuilderHelper } from '../../../../stores';
import { TabularCheckItemObserver } from '../../../common/editor';

const styles = require('../../../common/editor.css');
const listStyles = require('../../../common/list.css');
const fonts = require('../../../../../ui/common/fonts.css');

interface ItemState {
  skill: Skill;
  workRoleSkill: WithDbId<WorkRoleSkill> | null;
}

type ItemStateMap = Record<DbKey<Skill>, ItemState>;

interface RoleBuilderEditorProps extends WithI18nProps {
  helper: RoleBuilderHelper;
  loadedSkills: LoadedMap;
  logUpdates(id: DbKey<Skill>, item: ChangeState<WorkRoleSkill>): void;
  debug?: boolean;
}

@observer
class RoleBuilderEditor0 extends React.Component<RoleBuilderEditorProps> {
  @observable fieldState: ItemStateMap;

  constructor(props: Readonly<RoleBuilderEditorProps>) {
    super(props);
    this.fieldState = this.loadFieldState();
  }

  /// ----- [ Initialize ] -----
  loadFieldState = (): ItemStateMap => {
    const { helper } = this.props;
    const fieldState: ItemStateMap = {};
    const availableSkills = helper.getAvailableSkills().reduce((acc, next) => acc.concat(next));
    for (const availableSkill of availableSkills) {
      const { attribute: skill } = availableSkill.value;
      // Initialize
      const itemState: ItemState = {
        skill: skill.value,
        workRoleSkill: null
      };

      if (availableSkill.kind === 'assigned') {
        const { item } = availableSkill.value;
        itemState.workRoleSkill = item;
      }

      fieldState[skill.id] = itemState;
    }
    return fieldState;
  };

  isSelected = (id: DbKey<Skill>) => {
    return this.isAssigned(id);
  };

  /// ----- [ Map accessors ] -----
  isAssigned = (id: DbKey<Skill>): boolean => {
    return this.fieldState[id].workRoleSkill !== null;
  };

  expected = (id: DbKey<Skill>): number | null => {
    const itemState = this.fieldState[id];
    return itemState && itemState.workRoleSkill !== null ? itemState.workRoleSkill.value.expected : null;
  };

  // industry = (id: DbKey<Skill>): FieldState<number> => {
  //     const itemState = this.fieldState.get(id)
  //     return itemState
  //       ? itemState.industry
  //       : withValueCallback(
  //           newIntegerFieldState(0, 100),
  //           this.logIndustry(id)
  //         )
  // }

  setAllExpected = (competencyId: DbKey<Competency>) => (value: number | null) => {
    /// Override all individual skill settings on the Competency;
    /// Set all to assigned;
    /// Set all to value passed in;
    /// Log changes
    runInAction(() => {
      for (const [k, v] of Object.entries(this.fieldState)) {
        if (v.skill.competencyId === competencyId) {
          this.logExpected(k)(value);
        }
      }
    });
  };

  /// ----- [ Log changes ] -----
  logExpected = (id: DbKey<Skill>) => (expected: number | null) => {
    if (expected !== null) {
      runInAction(() => {
        const itemState = this.fieldState[id];
        if (itemState && itemState.workRoleSkill !== null) {
          itemState.workRoleSkill.value.expected = expected;
          this.logUpdate(id, ActionType.update, itemState);
        } else {
          const newItemState: ItemState = {
            ...itemState,
            workRoleSkill: {
              id: '',
              value: {
                workRoleId: this.props.helper.workrole.id,
                skillId: id,
                expected,
                industryAverage: 0,
                skillGroupHeadings: new Array<string>()
              }
            }
          };
          this.fieldState[id] = newItemState;
          this.logUpdate(id, ActionType.create, newItemState);
        }
      });
    }
  };

  @action
  toggleAssigned = (id: DbKey<Skill>) => {
    const itemState = this.fieldState[id];
    if (itemState && itemState.workRoleSkill !== null) {
      const oldItemState = { ...itemState };
      this.fieldState[id].workRoleSkill = null;
      this.logUpdate(id, ActionType.delete, oldItemState);
    } else {
      // try to retrieve loaded state if it exists
      const loaded = this.props.loadedSkills.get(id);
      return this.logExpected(id)(loaded && loaded.kind === 'assigned' ? loaded.value.item.value.expected : 0);
    }
  };

  @action
  setAssigned = (id: DbKey<Skill>) => {
    const itemState = this.fieldState[id];
    if (!itemState || itemState.workRoleSkill === null) {
      // try to retrieve loaded state if it exists
      const loaded = this.props.loadedSkills.get(id);
      return this.logExpected(id)(loaded && loaded.kind === 'assigned' ? loaded.value.item.value.expected : 0);
    }
  };

  /// TODO: Problem: Not consistently checking roleSkillId here, relying on manipulation of action
  logUpdate = (id: DbKey<Skill>, act: ActionType, item: ItemState) => {
    const { logUpdates, helper } = this.props;
    switch (act) {
      case ActionType.create:
        {
          logUpdates(id, {
            kind: 'new',
            value: {
              skillId: id,
              expected: item.workRoleSkill !== null ? item.workRoleSkill.value.expected : 0,
              industryAverage: item.workRoleSkill !== null ? item.workRoleSkill.value.industryAverage : 0,
              skillGroupHeadings: item.workRoleSkill !== null ? item.workRoleSkill.value.skillGroupHeadings : new Array<string>(),
              workRoleId: helper.workrole.id
            }
          });
        }
        break;
      case ActionType.update:
        {
          logUpdates(id, {
            kind: 'update',
            value: {
              id: item.workRoleSkill !== null ? item.workRoleSkill.id : '',
              value: {
                skillId: id,
                expected: item.workRoleSkill !== null ? item.workRoleSkill.value.expected : 0,
                industryAverage: item.workRoleSkill !== null ? item.workRoleSkill.value.industryAverage : 0,
                skillGroupHeadings: item.workRoleSkill !== null ? item.workRoleSkill.value.skillGroupHeadings : new Array<string>(),
                workRoleId: helper.workrole.id
              }
            }
          });
        }
        break;
      case ActionType.delete:
        {
          logUpdates(id, {
            kind: 'delete',
            value: item.workRoleSkill !== null ? item.workRoleSkill.id : ''
          });
        }
        break;
      default:
        return;
    }
  };

  /// ----- [ Input Components ] -----
  SkillAssignedObserver = observer(({ id }) =>
    TabularCheckItemObserver({
      field: this.isAssigned(id),
      id,
      onChange: this.toggleAssigned
    })
  );

  SkillExpectedObserver = observer(({ id }) => {
    // Call only once
    const expected = this.expected(id);
    return (
      <ScoreSlider
        ranges={this.props.helper.workrole.scoreRanges.map(item => item.value)}
        onChange={this.logExpected(id)}
        value={expected === null ? undefined : expected}
        halfwayMarks
        noZero
      />
    );
  });

  CompetencyExpectedObserver = observer(({ id, expected }) => {
    return (
      <ScoreSlider
        ranges={this.props.helper.workrole.scoreRanges.map(item => item.value)}
        onChange={this.setAllExpected(id)}
        value={expected === null ? undefined : expected}
        halfwayMarks
        noZero
      />
    );
  });

  // _IndustryAverageObserver = observer(({ id, index }) =>
  //   TabularTextInputObserver({
  //       field: this.industry(id),
  //       type: "number",
  //       placeholder: "",
  //       id,
  //       key: index
  //   })
  // );

  renderSkillName = (skill: Skill) => {
    const displayName = this.props.t(skill.name);
    return <span className={fonts.caption}>{displayName}</span>;
  };

  renderSkillDefinitionAndExpected = (skill: WithDbId<Skill>) => {
    const definition = this.props.t(skill.value.definition);
    return (
      <>
        <div className={styles.definition}>
          <span className={fonts.caption}>{definition}</span>
        </div>
        <div className={styles.definition}>
          <this.SkillExpectedObserver id={skill.id} />
        </div>
      </>
    );
  };

  renderCompetencyDefinitionAndExpected = (item: WithDbId<Competency>, expected: number | null) => {
    const definition = this.props.t(item.value.definition);
    return (
      <>
        <div className={styles.definition}>
          <span className={fonts.caption}>{definition}</span>
        </div>
        <div className={styles.definition}>
          <this.CompetencyExpectedObserver id={item.id} expected={expected} />
        </div>
      </>
    );
  };

  renderCompetencyTable = (
    competency: WithDbId<Competency>,
    _index: number,
    _assignedSkillsCount: number,
    _allSkillsCount: number,
    expected: number
  ) => {
    return (
      <Table className={classNames(listStyles.noBorder, listStyles.noLeftMargin)}>
        <TableRow>
          <TableCell>{''}</TableCell>
          <TableCell>{this.renderCompetencyDefinitionAndExpected(competency, expected)}</TableCell>
          {this.props.debug && <TableCell key={3}>{Math.round(expected)}</TableCell>}
        </TableRow>
      </Table>
    );
  };

  renderSkillTable = (competencySkillSet: ItemStateMap) => {
    return (
      <Table className={classNames(listStyles.noBorder, listStyles.noLeftMargin)}>
        {Object.entries(competencySkillSet).map(([key, value]) => {
          return (
            <TableRow key={key}>
              <this.SkillAssignedObserver id={key} />
              <TableCell>{this.renderSkillName(value.skill)}</TableCell>
              <TableCell>
                {this.renderSkillDefinitionAndExpected(makeWithDbId({ id: key, value: value.skill }))}
              </TableCell>
              {this.props.debug && (
                <TableCell key={2}>
                  <span>{value.workRoleSkill !== null ? value.workRoleSkill.value.expected : ''}</span>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </Table>
    );
  };

  renderCompetencySet = (competency: WithDbId<Competency>, index: number) => {
    const competencySkillSet = Object.entries(this.fieldState).reduce((acc: ItemStateMap, [k, v]) => {
      if (v.skill.competencyId === competency.id) {
        acc[k] = v;
      }
      return acc;
    }, {});
    const countAvailable = Object.keys(competencySkillSet).length;
    const countAssigned = Object.values(competencySkillSet).filter(item => item.workRoleSkill !== null).length;
    if (countAvailable > 0) {
      // const assignedForCompetency = Object.values(competencySkillSet).filter(item => item.workRoleSkill !== null)
      // const countAssigned = assignedForCompetency.length
      // const avgExpectedForCompetency =
      //   countAssigned === 0
      //     ? 0
      //     : Math.floor(assignedForCompetency.reduce((s, item) =>
      //         item.workRoleSkill !== null ? s + item.workRoleSkill.value.expected : s, 0)/countAssigned)
      const skillCountText =
        countAssigned === 0 ? 'No' : countAssigned === countAvailable ? 'All' : `${countAssigned} of ${countAvailable}`;
      const headerContent = `${skillCountText} available skills included.`;
      return (
        <Accordion
          HeaderIcon={HeartBeatIcon}
          headerTitle={this.props.t(competency.value.fullName)}
          // headerSubtitle={headerSubTitle}
          headerContent={<span>{headerContent}</span>}
          key={competency.id}
          collapsedContent={this.renderCompetencyTable(
            competency,
            index,
            countAssigned,
            countAvailable,
            // avgExpectedForCompetency
            this.props.helper.expected({ kind: 'competency', competencyId: competency.id })
          )}
          expandedContent={this.renderSkillTable(competencySkillSet)}
          expanderLabels={['Hide Skills', 'Show Skills']}
        />
      );
    } else {
      return null;
    }
  };

  render() {
    return this.props.helper.getAvailableCompetenciesWithKeys().map(this.renderCompetencySet);
  }
}

export const RoleBuilderEditor = withI18n(RoleBuilderEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/role-builder-editor/role-builder-editor.tsx